// Client-side mirror of the backend prerequisite checks in
// apps/stripe/payment_links.py (_validate_prerequisites / _validate_email /
// _validate_whatsapp / _validate_stripe). Lets the "Send link" dialog and the
// Payments-tab Resend menu disable a channel *before* the user clicks Send,
// with the same reason the backend would otherwise only return after a
// rejected request.
//
// This is UI guidance only, not an authorization decision — the backend is
// still the single source of truth (see PrerequisitesFailed.channels / .detail
// surfaced via extractApiError), and every code here maps 1:1 to a backend
// block `code` so a change on one side is easy to notice on the other:
//   stripe_not_connected, email_template_missing, email_missing,
//   email_invalid, whatsapp_template_missing, whatsapp_not_connected,
//   whatsapp_template_not_approved, whatsapp_template_incompatible,
//   phone_missing, phone_invalid, not_contactable.

export const READINESS_REASON = {
    stripe_not_connected:
        "Stripe isn't connected for this practice — connect Stripe in Settings to send payment links",
    email_template_missing: 'Payment link email template not set up',
    email_missing: 'No email on file',
    email_invalid: 'Enter a valid email address',
    whatsapp_template_missing: 'WhatsApp payment link template not set up',
    whatsapp_not_connected: 'WhatsApp not connected',
    whatsapp_template_not_approved: 'WhatsApp template awaiting Meta approval',
    whatsapp_template_incompatible: 'WhatsApp payment link template is not compatible',
    phone_missing: 'No phone on file',
    phone_invalid: 'Enter a valid phone number',
    not_contactable: 'This lead is not contactable',
};

// Codes where nothing the user types into the dialog can fix it — these
// disable the channel's checkbox/menu-item outright. Missing/invalid contact
// details are surfaced as a hint instead of disabling the control, because
// the dialog (and the backend's `email`/`phone` override params) let staff
// type a value right there — disabling the checkbox would make that
// impossible.
const BLOCKING_CODES = new Set([
    'stripe_not_connected',
    'email_template_missing',
    'whatsapp_template_missing',
    'whatsapp_not_connected',
    'whatsapp_template_not_approved',
    'whatsapp_template_incompatible',
    'not_contactable',
]);

function isContactable(leadDetail) {
    if (!leadDetail) return true;
    if (leadDetail.is_archived) return false;
    if (leadDetail.is_open_for_communication === false) return false;
    return true;
}

function buildChannel(code) {
    return {
        ready: !code,
        code: code || null,
        reason: code ? (READINESS_REASON[code] || 'Not available') : null,
        blocking: code ? BLOCKING_CODES.has(code) : false,
    };
}

/**
 * @param {object} params
 * @param {object} [params.leadDetail] - the lead, for the not_contactable check.
 * @param {boolean} params.hasEmailOnFile - lead has a contact email (default or typed override).
 * @param {boolean} params.hasPhoneOnFile - lead has a contact phone (default or typed override).
 * @param {object} [params.practiceSettings] - `practice.settings` (payment_link_email_template / payment_link_whatsapp_template ids).
 * @param {boolean|undefined} params.stripeConnected - undefined while still loading (treated as "unknown, don't block yet").
 * @param {{connected: boolean, results: Array}|null|undefined} params.whatsappEligibility -
 *   Contract C response (`GET .../payment-link-whatsapp-templates/`); null/undefined while loading.
 * @returns {{stripeConnected: boolean|undefined, stripeReason: string|null, email: object, whatsapp: object}}
 */
export function getPaymentLinkChannelReadiness({
    leadDetail,
    hasEmailOnFile,
    hasPhoneOnFile,
    practiceSettings,
    stripeConnected,
    whatsappEligibility,
} = {}) {
    // `practiceSettings` itself being null/undefined means the practice
    // record hasn't loaded yet (e.g. `state.practice.practice.settings`
    // before `getPracticeItem` resolves) — unknown, not "nothing configured".
    // Once it's loaded (even to `{}`), a missing template id is a real block.
    const settingsLoaded = practiceSettings != null;
    const settings = practiceSettings || {};
    const contactable = isContactable(leadDetail);
    // stripeConnected === undefined means "still loading" — don't block yet.
    const stripeKnownDisconnected = stripeConnected === false;
    const stripeCode = stripeKnownDisconnected ? 'stripe_not_connected' : null;

    let emailCode = null;
    if (stripeCode) emailCode = stripeCode;
    else if (!contactable) emailCode = 'not_contactable';
    else if (settingsLoaded && !settings.payment_link_email_template) emailCode = 'email_template_missing';
    else if (!hasEmailOnFile) emailCode = 'email_missing';

    let whatsappCode = null;
    if (stripeCode) {
        whatsappCode = stripeCode;
    } else if (!contactable) {
        whatsappCode = 'not_contactable';
    } else if (whatsappEligibility && whatsappEligibility.connected === false) {
        whatsappCode = 'whatsapp_not_connected';
    } else if (settingsLoaded && !settings.payment_link_whatsapp_template) {
        whatsappCode = 'whatsapp_template_missing';
    } else if (settingsLoaded && whatsappEligibility && Array.isArray(whatsappEligibility.results)) {
        // Contract C only lists APPROVED templates for the connected WABA, so
        // the configured template being absent from `results` means it's no
        // longer approved (deleted/rejected practices never reach here — the
        // FK write itself is gated on APPROVED at save time).
        const configured = whatsappEligibility.results.find(
            (t) => String(t.id) === String(settings.payment_link_whatsapp_template),
        );
        if (!configured) whatsappCode = 'whatsapp_template_not_approved';
        else if (!configured.compatible) whatsappCode = 'whatsapp_template_incompatible';
    }
    if (!whatsappCode && !hasPhoneOnFile) whatsappCode = 'phone_missing';

    return {
        stripeConnected: stripeConnected === undefined ? undefined : Boolean(stripeConnected),
        stripeReason: stripeCode ? READINESS_REASON[stripeCode] : null,
        email: buildChannel(emailCode),
        whatsapp: buildChannel(whatsappCode),
    };
}
